// packages/report-components/src/KeyTakeaways.tsx
//
// The numbered takeaway list that sits beside the trend chart in the
// executive readout. Each row is a ranked finding: a sequence number, a
// short title, one or two sentences of explanation, and an optional
// quantified chip on the right ("+€28.4k", "−0.5x", "31.8% vs 32%").
//
// Division of labour, per the report-templates plan:
//   - `title` / `body` are narrative slots — the author writes them, the
//     narrative-refresh path may rewrite them.
//   - `value` is a NUMBER THE REPORT ALREADY SHOWS. It is a formatted
//     string here because every hook returns pre-formatted display
//     values, but it must be lifted from hook data, never invented as
//     free text. The prompt states this; this component cannot enforce
//     it, so the rule is written where the author reads it.
//
// `sentiment` colours the chip and is independent of the sign of the
// value: a falling CPA is good news. Default is neutral.

import * as React from "react";

import { pageListItemPx, usePageDensity, type PageDensity } from "./PageDensityContext";
import { PageEyebrow } from "./pageFrameParts";

export type TakeawaySentiment = "positive" | "negative" | "neutral";

export type KeyTakeaway = {
  /** Short finding headline ("Meta drove the upside"). */
  title: string;
  /** One or two sentences of explanation. */
  body?: string;
  /** Optional quantified chip, lifted from hook data. */
  value?: string;
  /** Chip colour. Defaults to neutral. */
  sentiment?: TakeawaySentiment;
};

export type KeyTakeawaysProps = {
  items: KeyTakeaway[];
  /** Block heading. Defaults to "Key takeaways". */
  title?: string;
  /** ON A REPORT PAGE only: how many lines of body every item reserves.
   * The server priced the cell with this same number (`page_list_item_px`),
   * so it is data the adapter hands down, not a style choice. Clamped to
   * 1..`list_max_body_lines`; ignored off a page, where the body flows. */
  bodyLines?: number;
};

/** Beyond six the list stops being a readout and becomes a dump. Extra
 * items are dropped at render; the prompt keeps the list short. */
const MAX_ITEMS = 6;

function chipColours(sentiment: TakeawaySentiment | undefined): {
  bg: string;
  fg: string;
} {
  if (sentiment === "positive") {
    return {
      bg: "var(--verity-green-0, #ebfbee)",
      fg: "var(--verity-green-9, #2b8a3e)",
    };
  }
  if (sentiment === "negative") {
    return {
      bg: "var(--verity-red-0, #fff5f5)",
      fg: "var(--verity-red-9, #c92a2a)",
    };
  }
  return {
    bg: "var(--verity-yellow-0, #fff9db)",
    fg: "var(--verity-yellow-9, #e67700)",
  };
}

// ---------------------------------------------------------------------------
// The page form of an analysis list
// ---------------------------------------------------------------------------
//
// A page cell clips and nothing reflows, so the server budgets how many items
// a list block holds from a pixel number (`page_list_item_px` in
// `services/composition/layout.py`, "Page density"). Until this step the drawn
// item was about 66px against a planned 45, and the third takeaway of a block
// was simply cut off the page (plan 2026-09-19-a-report-page-has-a-house-style,
// symptom 4; Arjen's page-by-page read of the paid-media demo report). Here the
// item DRAWS the budgeted number: a one-line title, a body slot of exactly
// `bodyLines` lines whatever the text does, fixed padding and the hairline. So
// k items are exactly k * `pageListItemPx` tall, by construction.
//
// `RecommendationList` imports these from this module, rather than growing a
// second implementation of the same boxes: on paper a recommendation IS a
// takeaway (the product decision behind step A4), and one drawn form means one
// place where the geometry can be wrong.
//
// Off a page none of this exists. `usePageDensity()` is null on the flowing
// report, in the mail and on the dashboard, and both components draw exactly
// what they always drew.

/** The body lines an item reserves on a page.
 *
 * Clamped to what the spec allows, because the server clamped the same way
 * when it decided how many items the cell gets (`page_list_item_px`): a
 * component that drew one line more would cost the block its last item. */
export function pageListBodyLines(density: PageDensity, requested: number | undefined): number {
  const asked = typeof requested === "number" && Number.isFinite(requested) ? Math.floor(requested) : 1;
  return Math.max(1, Math.min(density.list_max_body_lines, asked));
}

/**
 * Marks every clamped box that lost text to its clamp with `data-truncated`.
 *
 * A clamped box never overflows its parent, which is exactly what makes the
 * page geometry hold and also what makes a cut-off sentence invisible: nothing
 * moves, nothing scrolls, the reader just gets less. The editor reads this
 * attribute to warn the author, so truncation leaves the drawing layer as
 * DATA rather than as a defect somebody has to spot by eye.
 *
 * A layout effect, after the boxes exist and before paint. Under jsdom every
 * box measures 0 by 0, so nothing is ever marked there: the component suites
 * cannot see this, and `apps/artifact-sandbox/measure` is where it is real.
 */
function useTruncationFlags(card: HTMLElement | null): void {
  React.useLayoutEffect(() => {
    if (!card) return;
    for (const el of card.querySelectorAll<HTMLElement>("[data-clamp]")) {
      // One pixel of slack: `scrollHeight` and `clientHeight` are rounded
      // integers, so a box whose content is 42.4px tall reports 43 against a
      // client height of 42 without a glyph having been dropped. A real
      // truncation costs a whole line (or, on the title, whole words).
      const clipped =
        el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1;
      if (clipped) el.setAttribute("data-truncated", "1");
      else el.removeAttribute("data-truncated");
    }
  });
}

export type PageListCardProps = {
  density: PageDensity;
  /** The card's own heading. One line, ellipsis past the card's width. */
  heading: string;
  className: string;
  testId?: string;
  children: React.ReactNode;
};

/**
 * The card an analysis list draws on a page: the frame's padding and border,
 * one heading line, the gap under it, then the items.
 *
 * It FILLS its cell, which is what makes two list blocks side by side the same
 * height (the row already gave every cell in it one height, `arrange_rows`).
 * The free space therefore falls at the BOTTOM: the heading and the list are
 * both `flex: 0 0 auto` in a column, so the items start directly under the
 * heading and an under-filled card does not float its content into the middle.
 *
 * Nothing here hides overflow. The CELL is the page's one clipper
 * (`cellStyle`), and a card that clipped its own content would take the
 * editor's overflow warning, and the browser test's `clippedBy`, away from the
 * author.
 */
export function PageListCard({
  density,
  heading,
  className,
  testId,
  children,
}: PageListCardProps): React.ReactElement {
  const [card, setCard] = React.useState<HTMLElement | null>(null);
  useTruncationFlags(card);
  return (
    <section
      ref={setCard}
      className={className}
      data-testid={testId}
      data-page-list="1"
      style={{
        height: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        // No card on a page (2026-09-22, Arjen: the house style for all
        // reports): no background, no border, no padding. Paper is the
        // surface; a border with padding around every list is a screen
        // tile's frame, and the space it took is the reader's. The flowing
        // card below keeps its lift.
      }}
    >
      {/* The label is the same eyebrow the executive summary and the focus
          band draw, so a page grows one kind of label and not three. */}
      <PageEyebrow density={density} className="verity-page-list__eyebrow" text={heading}>
        {heading}
      </PageEyebrow>
      <ol
        style={{
          flex: "0 0 auto",
          listStyle: "none",
          margin: 0,
          padding: 0,
        }}
      >
        {children}
      </ol>
    </section>
  );
}

export type PageListItemProps = {
  density: PageDensity;
  /** 1-based position, drawn in the numeral. */
  n: number;
  /** The last item draws the hairline transparent rather than not at all, so
   * every item costs the same pixel and k items are k * the item's height. */
  last: boolean;
  bodyLines: number;
  className: string;
  title: string;
  body?: string;
  /** One line under the body (a recommendation's impact, owner, due date and
   * evidence button). Content, not chrome: it makes the item one body line
   * taller and says so in `data-meta-line`. */
  meta?: React.ReactNode;
  /** Sits to the right of the text column (the takeaways' value chip). */
  trailing?: React.ReactNode;
};

/** One item of an analysis list on a page. Its height is
 * `pageListItemPx(density, bodyLines)`, plus one body line when it carries a
 * meta line, and never depends on how much text it was given. */
export function PageListItem({
  density,
  n,
  last,
  bodyLines,
  className,
  title,
  body,
  meta,
  trailing,
}: PageListItemProps): React.ReactElement {
  const bodyHeight = bodyLines * density.list_body_line_px;
  const height =
    pageListItemPx(density, bodyLines) + (meta ? density.list_body_line_px : 0);
  return (
    <li
      className={className}
      data-list-item=""
      data-body-lines={bodyLines}
      data-meta-line={meta ? "1" : undefined}
      data-last-item={last ? "1" : undefined}
      style={{
        boxSizing: "border-box",
        height,
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        listStyle: "none",
        paddingTop: density.list_item_pad_y_px,
        paddingBottom: density.list_item_pad_y_px,
        // Transparent on the last item rather than absent: see `last` above.
        borderBottom: `${density.list_rule_px}px solid ${
          last ? "transparent" : "var(--verity-color-border-subtle, rgba(0,0,0,0.06))"
        }`,
      }}
    >
      <PageNumeral n={n} />
      <span style={{ flex: "1 1 auto", minWidth: 0 }}>
        <span
          data-clamp="title"
          title={title}
          style={{
            display: "block",
            height: density.list_title_line_px,
            lineHeight: `${density.list_title_line_px}px`,
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            fontSize: "var(--verity-font-size-md, 15px)",
            fontWeight: 600,
            color: "var(--verity-color-ink, var(--verity-color-text, #111827))",
          }}
        >
          {title}
        </span>
        <span
          data-clamp="body"
          style={{
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: bodyLines,
            overflow: "hidden",
            // Fixed, not `max-height`: a short body must reserve the same
            // space as a long one, or two items of the same budget draw two
            // different heights and the block loses its last one.
            height: bodyHeight,
            marginTop: density.list_title_body_gap_px,
            lineHeight: `${density.list_body_line_px}px`,
            fontSize: "var(--verity-font-size-sm, 13px)",
            color: "var(--verity-color-text-muted, #6b7280)",
            wordBreak: "break-word",
          }}
        >
          {body ?? ""}
        </span>
        {meta}
      </span>
      {trailing}
    </li>
  );
}

/** The numeral both lists draw on a page: the takeaways' 24px soft circle.
 * `RecommendationList`'s 32px solid circle is its flowing form only. */
function PageNumeral({ n }: { n: number }): React.ReactElement {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "0 0 auto",
        width: 24,
        height: 24,
        borderRadius: "var(--verity-radius-full, 9999px)",
        background: "var(--verity-color-accent-soft, #EEF2FC)",
        color: "var(--verity-color-primary, #3455D3)",
        fontSize: "var(--verity-font-size-xs, 12px)",
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {n}
    </span>
  );
}

/** The one meta line a recommendation draws on a page: everything it carries,
 * on one line of body height, clipped with an ellipsis past the card's width.
 * Exported here because the line belongs to the page list form; what goes IN
 * it is `RecommendationList`'s business. */
export function PageMetaLine({
  density,
  className,
  children,
}: {
  density: PageDensity;
  className?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <span
      className={className}
      data-clamp="meta"
      style={{
        display: "block",
        height: density.list_body_line_px,
        lineHeight: `${density.list_body_line_px}px`,
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        fontSize: "var(--verity-font-size-xs, 12px)",
        color: "var(--verity-color-text-muted, #6b7280)",
      }}
    >
      {children}
    </span>
  );
}

export function KeyTakeaways({
  items,
  title = "Key takeaways",
  bodyLines,
}: KeyTakeawaysProps): React.ReactElement | null {
  // Non-null only on a report PAGE (plan 2026-09-19 report page house style,
  // A4). There the card fills its cell and every item is exactly as tall as
  // the server budgeted it. Off a page this is null and the list below draws
  // what it always drew.
  const density = usePageDensity();
  const safeItems = Array.isArray(items)
    ? items.filter((i) => i && typeof i.title === "string" && i.title.trim().length > 0)
    : [];
  if (safeItems.length === 0) return null;
  const shown = safeItems.slice(0, MAX_ITEMS);

  if (density) {
    const lines = pageListBodyLines(density, bodyLines);
    return (
      <PageListCard
        density={density}
        heading={title}
        className="verity-key-takeaways"
        testId="key-takeaways"
      >
        {shown.map((item, i) => {
          const { bg, fg } = chipColours(item.sentiment);
          return (
            <PageListItem
              key={i}
              density={density}
              n={i + 1}
              last={i === shown.length - 1}
              bodyLines={lines}
              className="verity-key-takeaways__item"
              title={item.title}
              body={item.body}
              trailing={
                item.value ? (
                  <span
                    className="verity-key-takeaways__chip"
                    style={{
                      flex: "0 0 auto",
                      padding: "4px 10px",
                      borderRadius: "var(--verity-radius-sm, 6px)",
                      background: bg,
                      color: fg,
                      fontSize: "var(--verity-font-size-sm, 13px)",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {item.value}
                  </span>
                ) : undefined
              }
            />
          );
        })}
      </PageListCard>
    );
  }

  return (
    <section
      className="verity-key-takeaways"
      data-testid="key-takeaways"
      style={{
        height: "100%",
        boxSizing: "border-box",
        background: "var(--verity-color-surface, #ffffff)",
        border: "1px solid var(--verity-color-border, #e5e7eb)",
        borderRadius: "var(--verity-radius-report, var(--verity-radius-lg, 12px))",
        padding: "var(--verity-space-lg, 24px)",
        boxShadow:
          "var(--verity-elevation-1, var(--verity-shadow-sm, 0 1px 2px rgba(0,0,0,0.05)))",
      }}
    >
      <h3
        style={{
          margin: 0,
          marginBottom: 12,
          fontSize: "var(--verity-font-size-lg, 17px)",
          fontWeight: 600,
          color: "var(--verity-color-ink, var(--verity-color-text, #111827))",
        }}
      >
        {title}
      </h3>
      <ol
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {shown.map((item, i) => {
          const { bg, fg } = chipColours(item.sentiment);
          return (
            <li
              key={i}
              className="verity-key-takeaways__item"
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                paddingTop: i === 0 ? 0 : 12,
                paddingBottom: i === shown.length - 1 ? 0 : 12,
                borderTop:
                  i === 0
                    ? undefined
                    : "1px solid var(--verity-color-border-subtle, rgba(0,0,0,0.06))",
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: "0 0 auto",
                  width: 24,
                  height: 24,
                  borderRadius: "var(--verity-radius-full, 9999px)",
                  background: "var(--verity-color-accent-soft, #EEF2FC)",
                  color: "var(--verity-color-primary, #3455D3)",
                  fontSize: "var(--verity-font-size-xs, 12px)",
                  fontWeight: 600,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {i + 1}
              </span>
              <span style={{ flex: "1 1 auto", minWidth: 0 }}>
                <span
                  style={{
                    display: "block",
                    fontSize: "var(--verity-font-size-md, 15px)",
                    fontWeight: 600,
                    color:
                      "var(--verity-color-ink, var(--verity-color-text, #111827))",
                  }}
                >
                  {item.title}
                </span>
                {item.body ? (
                  <span
                    style={{
                      display: "block",
                      marginTop: 4,
                      fontSize: "var(--verity-font-size-sm, 13px)",
                      lineHeight: "var(--verity-line-height-normal, 1.5)",
                      color: "var(--verity-color-text-muted, #6b7280)",
                    }}
                  >
                    {item.body}
                  </span>
                ) : null}
              </span>
              {item.value ? (
                <span
                  className="verity-key-takeaways__chip"
                  style={{
                    flex: "0 0 auto",
                    padding: "4px 10px",
                    borderRadius: "var(--verity-radius-sm, 6px)",
                    background: bg,
                    color: fg,
                    fontSize: "var(--verity-font-size-sm, 13px)",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {item.value}
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
